import { Button, Grid, Popover, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import CompanyNamePICModel from './models/CompanyNamePICModel';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import Swal from 'sweetalert2';
import CompanyNamePICApi, { DeleteCompanyNamePICByIdParam } from './api/CompanyNamePICApi';
import { CompanyNamePICSearchSelector, setCheckedCompanyNamePICs, setDatatableSelectedCompanyNamePICIndex, setOrderBy, setOrderDirection, setTriggerCompanyNamePICSearch } from './reducers/CompanyNamePICSearchReducer';
import { CompanyNamePICInputSelector, setSelectedCompanyNamePICId } from './reducers/CompanyNamePICInputReducer';

const CompanyNamePICTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let history = useHistory();

    let companyNamePICData = useSelector(CompanyNamePICSearchSelector);
    const companyNamePICInputViewData = useSelector(CompanyNamePICInputSelector);
    const userProfile = useSelector(userProfileSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [overviewFullTc, setOverviewFullTc] = useState<string>("");

    const divRef = React.createRef<HTMLDivElement>();

    const columns = [
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("companyName"),
            Title: "Company Name",
            AllowSort: true,
        } as DatatableColumnModel<CompanyNamePICModel>,
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("pic"),
            Title: "PIC",
            AllowSort: true,
        } as DatatableColumnModel<CompanyNamePICModel>,
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("emailTo"),
            Title: "Email To",
            AllowSort: true,
        } as DatatableColumnModel<CompanyNamePICModel>,
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("emailCc"),
            Title: "Email Cc",
            AllowSort: true,
        } as DatatableColumnModel<CompanyNamePICModel>,
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("contactDetails"),
            Title: "Contact Details",
            AllowSort: true,
            Render: (rowData: CompanyNamePICModel) => {
                let previewButton = rowData.contactDetails && rowData.contactDetails.length > 0 ?
                    <i className="fa fa-eye" aria-hidden="true"></i> :
                    <i className="fa fa-eye-slash" aria-hidden="true"></i>;
                return <>
                    <Button
                        className="tonnage-table-preview-full-tc"
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.contactDetails && rowData.contactDetails.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.contactDetails);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                        disabled={!rowData.contactDetails || rowData.contactDetails.length === 0}
                    >
                        {previewButton}
                    </Button>
                </>
            }
        } as DatatableColumnModel<CompanyNamePICModel>,
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("userDisplayName"),
            Title: "Broker",
            AllowSort: true,
        } as DatatableColumnModel<CompanyNamePICModel>,
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("updatedDate"),
            Title: "Updated Date",
            AllowSort: true,
            Render: (rowData: CompanyNamePICModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<CompanyNamePICModel>,
        {
            Field: "",
            Title: "",
            AllowSort: true,
            Render: (rowData: CompanyNamePICModel) => (
                <>
                    {userProfile.userGroup === "Administrator" &&
                        <Tooltip title="Delete CompanyNamePIC">
                            <Button
                                className='table-action-button'
                                variant='outlined'
                                color='error'
                                onClick={() => {
                                    deleteCompanyNamePIC(rowData.id)
                                }}
                            >
                                <i className="fa fa-trash" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                    }
                </>
            )
        } as DatatableColumnModel<CompanyNamePICModel>,
    ];

    const columnsForCopy = [
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("companyName"),
            Title: "Company Name",
            AllowSort: true,
        } as DatatableColumnModel<CompanyNamePICModel>,
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("pic"),
            Title: "PIC",
            AllowSort: true,
        } as DatatableColumnModel<CompanyNamePICModel>,
        {
            Field: Utility.propertyOf<CompanyNamePICModel>("contactDetails"),
            Title: "Contact Details",
            AllowSort: true,
        } as DatatableColumnModel<CompanyNamePICModel>,
    ];

    const deleteCompanyNamePIC = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this market info? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                CompanyNamePICApi.Delete({
                    id: id
                } as DeleteCompanyNamePICByIdParam)
                    .then((response: BaseResponse<CompanyNamePICModel>) => {
                        if (response.success) {
                            Utility.Alert.Success("CompanyNamePIC deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerCompanyNamePICSearch(new Date().getTime()));
                        }
                    });
            }
        })
    }

    const formatUpdatedDate = (dateString: string): string => {
        if (!dateString) {
            return "";
        }
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const getTableClassName = () => {
        let className = "companyNamePIC-datatable-search-result nice-scroll";

        if (!companyNamePICInputViewData.companyNamePICInputExpand) {
            className += " companyNamePIC-datatable-search-result-quick-input-collapse-only";
        }

        return className;
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let div = document.getElementById("table-copy");
                        if (!div) {
                            return;
                        }

                        div.className = "table-copy-show";

                        let selection = window.getSelection();
                        if (selection) {
                            let range = document.createRange();
                            range.selectNodeContents(divRef.current as Node);
                            selection.removeAllRanges();
                            selection.addRange(range);
                            document.execCommand("copy");
                            Utility.Alert.Success("Copied to clipboard");
                        }

                        div.className = "table-copy-hide";
                    }}
                >Copy contact details to clipboard</Button>
            </Grid>
            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {companyNamePICData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<CompanyNamePICModel>
            useCheckBox={true}
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={companyNamePICData.searchResults}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<CompanyNamePICModel>, orderDirection: OrderDirection) => {
                dispatch(setOrderBy(column.Field));
                dispatch(setOrderDirection(orderDirection));
                dispatch(setTriggerCompanyNamePICSearch((new Date()).getTime()));
            }}
            onRowSelectedChange={(tonnage: CompanyNamePICModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedCompanyNamePICId(tonnage.id))
                    dispatch(setDatatableSelectedCompanyNamePICIndex(index));
                }
            }}
            selectedRowIndex={companyNamePICData.datatableSelectedIndex}
            stickyHeader={true}
            orderColumn={companyNamePICData.query.orderBy}
            orderDirection={companyNamePICData.query.orderDirection}
            onTableCheckboxSelectedChange={(selectedRows: CompanyNamePICModel[]) => {
                dispatch(setCheckedCompanyNamePICs(selectedRows));
            }}
        ></Datatable>
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }}>{overviewFullTc}</Typography>
        </Popover>
        <div id="table-copy"
            className='table-copy-hide'
            contentEditable="true"
            ref={divRef}
        >
            <Datatable<CompanyNamePICModel>
                useDefaultSearchBar={false}
                pagingType={PagingTypeEnum.None}
                data={companyNamePICData.checkedCompanyNamePICs}
                useItemPerPage={false}
                columns={columnsForCopy}
                isDataLoaded={true}
                className={getTableClassName()}
            ></Datatable>
        </div>
    </div>
};

export default CompanyNamePICTable;